import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Button, Card, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { BsExclamationTriangleFill } from 'react-icons/bs';
import AppContext from 'Utils/AppContext';
import { BRAND, INK, STATUS, FONT_MONO, statusToSemantic } from 'Utils/styleTokens';
import { sourceLabels } from './Components';
import TagsPill from './TagsPill';
import { startStopService, isReadOnlySource, platformResourceReason } from '../manage-api';
import { pickerCloudAccounts } from '../../CloudAccounts/api';
import {
  isCategory,
  categoryForSource,
  kindForSource,
  inferCloud,
  isInternalExposure,
  isLoudExposure,
  rangeProvenanceLabel,
  sortPortExposures,
  portRangeLabel,
  getHideInternalPref,
  setHideInternalPref,
} from '../LandscapeUtils';
import {
  findMatchingConnection,
  managerSourceToEditorPrefill,
  encodePrefill,
} from 'SensitiveDataLandscape/UnknownSources/managerSources';

// Per-source pill colors — matches mockup-6a (EC2 amber, RDS blue, S3 red). Any
// unknown / non-AWS source key falls back to a cloud-tinted neutral so azure_*/gcp_*
// keys still render a sensible chip.
const SOURCE_PILL = {
  ec2: { bg: STATUS.warning.bg, color: STATUS.warning.text },
  rds: { bg: STATUS.info.bg, color: STATUS.info.text },
  s3: { bg: STATUS.danger.bg, color: STATUS.danger.text },
};
const CLOUD_PILL = {
  aws: { bg: STATUS.warning.bg, color: STATUS.warning.text },
  azure: { bg: STATUS.info.bg, color: STATUS.info.text },
  gcp: { bg: '#E9EEFF', color: BRAND.blue },
};
const sourcePillStyle = (source) =>
  SOURCE_PILL[source] || CLOUD_PILL[inferCloud(source)] || STATUS.neutral;

// createTime is a mix of ms epochs (EC2) and second epochs (RDS) across providers.
// Only render values that look like a real ms epoch (post-2020) so second-epoch and
// zero values fall through to a dash — mirrors the prior MaterialTable render.
const createdLabel = (createTime) =>
  createTime > 11707249302 ? new Date(createTime).toLocaleDateString() : '—';

// Responsive column track — fixed columns stay compact and the ID/Name and Data-source
// columns flex to fill. Engine/IP, instance class and created date live in the row's
// expandable details drawer (with the port exposures) rather than as columns. MIN_WIDTH
// keeps the grid legible; the row block scrolls horizontally below that rather than
// clipping the right-hand column off-panel.
const GRID = 'minmax(170px,1fr) 52px minmax(340px,3fr) 80px 92px 82px';
const GRID_WITH_ACCOUNT = GRID + ' minmax(110px,150px)';
const MIN_WIDTH = 820;
const MIN_WIDTH_WITH_ACCOUNT = 950;

// Which AWS account a row was discovered under. The manager stamps `accountNumber` (12-digit
// account) and `accountName` (Cloud Account display name) on every row; `accountId` is the
// Cloud Account UUID (null for the pod's own workload identity). Older landscape files carry
// only `accountId`, so the name is resolved through the cloud-account picker as a fallback.
const PLATFORM_IDENTITY_LABEL = 'Platform identity';
export const accountKey = (row) => row.accountId || row.accountNumber || 'ambient';
export const accountLabel = (row, pickerNames) => {
  if (row.accountName) return row.accountName;
  const picked = row.accountId && pickerNames ? pickerNames[row.accountId] : null;
  if (picked) return picked;
  if (row.accountNumber) return row.accountNumber;
  if (row.accountId) return String(row.accountId).slice(0, 8);
  return PLATFORM_IDENTITY_LABEL;
};
export const accountTitle = (row, pickerNames) => {
  const label = accountLabel(row, pickerNames);
  if (row.accountNumber && label !== row.accountNumber) return `${label} · ${row.accountNumber}`;
  return label;
};

// Column header. When `sortKey` is given the header is a button that cycles the table's
// sort (asc → desc) and shows the active direction; the indicator is reserved-width so
// headers don't shift when the sort moves between columns.
const HeaderCell = ({ children, align, sortKey, sort, onSort }) => {
  const style = { textAlign: align || 'left', justifySelf: align === 'right' ? 'end' : 'start' };
  if (!sortKey) return <div style={style}>{children}</div>;
  const active = sort && sort.key === sortKey;
  const dir = active ? sort.dir : null;
  return (
    <div
      role='columnheader'
      tabIndex={0}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      data-testid={`sort-${sortKey}`}
      onClick={() => onSort(sortKey)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSort(sortKey);
        }
      }}
      title={active ? `Sorted ${dir === 'asc' ? 'ascending' : 'descending'} — click to reverse` : 'Click to sort'}
      style={{
        ...style,
        cursor: 'pointer',
        userSelect: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        color: active ? INK[700] : undefined,
      }}
    >
      {children}
      <span aria-hidden='true' style={{ width: 8, fontSize: 8, color: active ? INK[700] : INK[300] }}>
        {dir === 'asc' ? '▲' : dir === 'desc' ? '▼' : ''}
      </span>
    </div>
  );
};

// Sort accessors, keyed by header. Each returns a comparable primitive for a row; `null`
// sorts last in both directions so blanks never lead. `connection` is resolved by the
// caller (it depends on the saved data-source list), the rest come straight off the row.
const rowLabel = (row) => row.database || row.name || row.identifier || '';

// State of a row's Manage toggle: 'on' (available / running / starting), 'off' (stopped or
// heading there), or null when the row has no live toggle (read-only object storage,
// serverless engines, resources hosting the platform). Shared by the Manage sort and the
// lifecycle filter so the two never disagree about what "on" means.
const isToggleOn = (row) => row.status === 'available' || row.status === 'running' || row.status === 'starting';
export const toggleState = (row) => {
  if (isReadOnlySource(row) || row.engineMode === 'serverless' || platformResourceReason(row)) return null;
  return isToggleOn(row) ? 'on' : 'off';
};

// Lifecycle filter options — value → which toggle states it keeps.
export const LIFECYCLE_FILTERS = [
  { value: 'all', label: 'Any state' },
  { value: 'on', label: 'Running' },
  { value: 'off', label: 'Stopped' },
  { value: 'unmanaged', label: 'Not manageable' },
];
export const matchesLifecycle = (row, filter) => {
  if (!filter || filter === 'all') return true;
  const state = toggleState(row);
  return filter === 'unmanaged' ? state === null : state === filter;
};

const SORT_ACCESSORS = {
  connection: (row, ctx) => (ctx && ctx.connectionName ? String(ctx.connectionName).toLowerCase() : null),
  manage: (row) => {
    // Toggle state: on (1) before off (0); rows with no toggle last.
    const state = toggleState(row);
    return state === null ? null : state === 'on' ? 1 : 0;
  },
  name: (row) => rowLabel(row).toLowerCase() || null,
  type: (row) => (row.source ? String(sourceLabels[row.source] || row.source).toLowerCase() : null),
  status: (row) => (row.status ? String(row.status).toLowerCase() : null),
  region: (row) => (row.region ? String(row.region).toLowerCase() : null),
  account: (row, ctx) => accountLabel(row, ctx && ctx.pickerNames).toLowerCase(),
};

// Stable sort of `rows` by `sort` ({key, dir}); `null` accessor values always go last and
// ties fall back to the identifier so the order is deterministic across re-renders.
export const sortRows = (rows, sort, connectionNameFor, pickerNames) => {
  if (!sort || !sort.key || !SORT_ACCESSORS[sort.key]) return rows;
  const accessor = SORT_ACCESSORS[sort.key];
  const mul = sort.dir === 'desc' ? -1 : 1;
  return rows
    .map((row, i) => ({
      row,
      i,
      v: accessor(row, { connectionName: connectionNameFor ? connectionNameFor(row) : null, pickerNames }),
    }))
    .sort((a, b) => {
      if (a.v === null && b.v === null) return a.i - b.i;
      if (a.v === null) return 1;
      if (b.v === null) return -1;
      if (a.v < b.v) return -1 * mul;
      if (a.v > b.v) return 1 * mul;
      return String(a.row.identifier || '').localeCompare(String(b.row.identifier || '')) || a.i - b.i;
    })
    .map((x) => x.row);
};

const Pill = ({ style, children }) => (
  <span
    style={{
      fontSize: 10,
      fontWeight: 600,
      letterSpacing: '.05em',
      padding: '3px 9px',
      borderRadius: 999,
      whiteSpace: 'nowrap',
      ...style,
    }}
  >
    {children}
  </span>
);

// Lifecycle toggle. Startable DB/server resources get an interactive switch (green =
// available/running); read-only object storage and serverless engines render a disabled
// static toggle since their lifecycle can't be managed here. Resources that host the
// platform itself (see platformResources.js) render a greyed-out static toggle, with the
// matched reason as the tooltip — the row stays fully visible and expandable.
const ManageToggle = ({ row, fetching, onToggle }) => {
  const platformReason = platformResourceReason(row);
  const readOnly = toggleState(row) === null;
  const status = row.status;
  const transitioning = status === 'starting' || status === 'stopping' || status === 'pending';
  // Static (read-only) toggles still show the resource's real state, knob and all.
  const on = isToggleOn(row);
  const disabled = readOnly || transitioning || fetching;
  return (
    <button
      type='button'
      data-testid='manage-toggle'
      data-platform-resource={platformReason ? 'true' : undefined}
      title={
        platformReason
          ? platformReason
          : readOnly
          ? 'Lifecycle managed by the cloud provider'
          : transitioning
          ? `${status.charAt(0).toUpperCase()}${status.slice(1)}… (the manager re-checks every few minutes)`
          : on
          ? 'Stop this resource'
          : 'Start this resource'
      }
      data-transitioning={transitioning ? 'true' : undefined}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) onToggle(row);
      }}
      style={{
        width: 36,
        height: 20,
        borderRadius: 999,
        border: 'none',
        padding: 2,
        display: 'flex',
        justifyContent: on ? 'flex-end' : 'flex-start',
        // Platform rows: greyed out regardless of state so it reads as "not yours to flip",
        // not as a live green switch that happens to be stuck.
        // Mid-transition: amber track, knob on the side it is heading to.
        background: platformReason
          ? INK[200]
          : transitioning
          ? STATUS.warning.dot
          : on
          ? STATUS.success.text
          : INK[200],
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: platformReason ? 0.45 : disabled && !readOnly ? 0.75 : 1,
      }}
    >
      <span
        style={{
          width: 16,
          height: 16,
          borderRadius: '50%',
          background: '#fff',
          boxShadow: '0 1px 2px rgba(33,38,48,.25)',
        }}
      />
    </button>
  );
};

// Left-most "C2 data source" cell: link to the existing source's editor when this resource
// is already a data source, a disabled note for stopped instances, otherwise a
// pre-filled "Connect data source" action.
const DataSourceCell = ({ row, connection }) => {
  if (connection) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
        <Link
          to={`/sources/source-editor/${encodeURIComponent(connection.id)}`}
          onClick={(e) => e.stopPropagation()}
          title='Open data source detail'
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 11,
            fontWeight: 600,
            color: STATUS.success.text,
            background: STATUS.success.bg,
            padding: '5px 12px',
            borderRadius: 999,
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          ✓ {connection.name}
        </Link>
      </div>
    );
  }

  if (row.status === 'stopped') {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
        <span
          style={{
            fontSize: 11.5,
            fontWeight: 600,
            color: INK[400],
            background: INK[100],
            padding: '6px 14px',
            borderRadius: 7,
            whiteSpace: 'nowrap',
          }}
        >
          Instance stopped
        </span>
      </div>
    );
  }

  const prefill = encodePrefill(managerSourceToEditorPrefill(row));
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
      <Link
        to={`/sources/source-editor?prefill=${encodeURIComponent(prefill)}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          fontSize: 11.5,
          fontWeight: 600,
          color: BRAND.blue,
          background: STATUS.info.bg,
          padding: '6px 14px',
          borderRadius: 7,
          whiteSpace: 'nowrap',
        }}
      >
        + Connect data source
      </Link>
    </div>
  );
};

// engine/ip column value: servers show their instance DNS/IP, object storage shows the
// object-store URL, databases show their engine.
const engineIpValue = (row) => {
  const kind = kindForSource(row.source);
  if (kind === 'servers' && row.instance) {
    if (row.instance.publicIpAddress) return `public/${row.instance.publicIpAddress}`;
    if (row.instance.privateDnsName) return row.instance.privateDnsName;
    if (row.instance.privateIpAddress) return row.instance.privateIpAddress;
  }
  if (kind === 'files') return row.connectionUrl || 'NA';
  return row.engine || '—';
};

// Long Azure/GCP identifiers are fully-qualified resource paths; show the trailing name.
const shortId = (id) => {
  const s = id || '';
  if (s.indexOf('/') === -1) return s;
  const parts = s.split('/').filter(Boolean);
  return parts[parts.length - 1] || s;
};

// Show at most this many port rows collapsed; the rest sit behind "+N more ports".
const PORTS_COLLAPSED_COUNT = 2;

const DETAIL_LABEL_STYLE = { fontSize: 10, fontWeight: 600, letterSpacing: '.08em', color: INK[400] };

// Per-resource facts that used to be columns: engine (databases) or host/IP (servers) or
// object-store URL (files), instance class, and created date. One label/value pair each.
const ResourceDetails = ({ row }) => {
  const kind = kindForSource(row.source);
  const engineIpLabel = kind === 'servers' ? 'HOST / IP' : kind === 'files' ? 'URL' : 'ENGINE';
  const facts = [
    [engineIpLabel, engineIpValue(row)],
    ['CLASS', row.instanceClass || '—'],
    ['CREATED', createdLabel(row.createTime)],
  ];
  if (row.identifier && shortId(row.identifier) !== row.identifier) {
    facts.push(['FULL ID', row.identifier]);
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 32px', marginBottom: 12 }} data-testid='resource-details'>
      {facts.map(([label, value]) => (
        <div key={label} style={{ minWidth: 0 }}>
          <div style={DETAIL_LABEL_STYLE}>{label}</div>
          <div
            title={String(value)}
            style={{ fontFamily: FONT_MONO, fontSize: 12, color: INK[700], wordBreak: 'break-all' }}
          >
            {value}
          </div>
        </div>
      ))}
    </div>
  );
};

// Expanded drawer under a row: resource details, then port exposures when there are any.
const RowDetails = ({ row, exposures }) => (
  <div style={{ padding: '4px 20px 14px 84px' }}>
    <ResourceDetails row={row} />
    {exposures.length > 0 && <PortExposures exposures={exposures} />}
  </div>
);

const PortExposures = ({ exposures }) => {
  const [showAll, setShowAll] = useState(false);
  // Most-likely-a-database first; the SOURCE column carries the allowing IP range (the
  // old internal/external wording lives on in tooltips and the loud-red emphasis only).
  const sorted = sortPortExposures(exposures);
  const visible = showAll ? sorted : sorted.slice(0, PORTS_COLLAPSED_COUNT);
  const hiddenCount = sorted.length - visible.length;
  return (
    <div>
      <div
        className='sg-eyebrow'
        style={{ fontSize: 10.5, color: INK[400], marginBottom: 8 }}
      >
        Port exposures
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '150px 140px 84px 1fr',
          rowGap: 6,
          fontSize: 12,
          maxWidth: 620,
        }}
      >
        <div style={DETAIL_LABEL_STYLE}>PORT</div>
        <div style={DETAIL_LABEL_STYLE}>DATABASE</div>
        <div style={DETAIL_LABEL_STYLE}>MATCH</div>
        <div style={DETAIL_LABEL_STYLE}>SOURCE</div>
        {visible.map((exp, i) => {
          const provenance = rangeProvenanceLabel(exp);
          const loud = isLoudExposure(exp);
          const internal = isInternalExposure(exp);
          const reachTitle = internal
            ? (exp.internalNetwork ? `Reachable only from within ${exp.internalNetwork}` : 'Reachable only from within the private network')
            : loud
              ? `Port ${exp.port} is reachable from an external source (${exp.sourceRange})`
              : undefined;
          return (
            <React.Fragment key={`${exp.port}-${exp.sourceRange}-${i}`}>
              <div style={{ fontFamily: FONT_MONO, color: INK[900] }} title={reachTitle}>
                {loud && (
                  <BsExclamationTriangleFill size={12} color={STATUS.danger.text} style={{ marginRight: 6, verticalAlign: '-1px' }} />
                )}
                {portRangeLabel(exp)}
                {provenance && (
                  <span style={{ marginLeft: 6, fontFamily: 'inherit', fontSize: 10.5, color: INK[400] }}>
                    ({provenance})
                  </span>
                )}
              </div>
              <div style={{ fontFamily: FONT_MONO, color: INK[500] }}>{exp.knownDatabase || '—'}</div>
              <div style={{ color: INK[500] }}>{exp.match || '—'}</div>
              <div
                style={{ fontFamily: FONT_MONO, color: loud ? STATUS.danger.text : INK[500], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                title={reachTitle || exp.sourceRange || ''}
              >
                {exp.sourceRange || '—'}
              </div>
            </React.Fragment>
          );
        })}
      </div>
      {hiddenCount > 0 && (
        <button
          type='button'
          onClick={() => setShowAll(true)}
          style={{ border: 'none', background: 'none', padding: 0, marginTop: 6, fontSize: 11.5, color: BRAND.blue, cursor: 'pointer' }}
        >
          +{hiddenCount} more port{hiddenCount === 1 ? '' : 's'}
        </button>
      )}
      {showAll && sorted.length > PORTS_COLLAPSED_COUNT && (
        <button
          type='button'
          onClick={() => setShowAll(false)}
          style={{ border: 'none', background: 'none', padding: 0, marginTop: 6, fontSize: 11.5, color: BRAND.blue, cursor: 'pointer' }}
        >
          show less
        </button>
      )}
    </div>
  );
};

// Apply the /manage/:source route key as a filter: category buckets, an individual
// source key, a status shortcut, 'all', or (fallback) a region name.
const narrow = (rows, key) => {
  if (!rows) return [];
  if (!key || key === 'all') return rows;
  if (isCategory(key)) return rows.filter((r) => categoryForSource(r.source) === key);
  if (key === 'available' || key === 'running') {
    return rows.filter((r) => r.status === 'available' || r.status === 'running');
  }
  const sourceKeys = new Set(rows.map((r) => r.source));
  if (sourceKeys.has(key)) return rows.filter((r) => r.source === key);
  return rows.filter((r) => r.region === key);
};

const ManagedSourcesTable = (props) => {
  const { connections } = useContext(AppContext) || {};
  const [rows, setRows] = useState(() => narrow(props.managedSources, props.source));
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  // Manage-toggle filter: running / stopped / not manageable (see LIFECYCLE_FILTERS). Defaults
  // to Running — the resources worth acting on — so stopped instances and unmanageable rows
  // stay out of the way until asked for.
  const [lifecycleFilter, setLifecycleFilter] = useState('on');
  // Column sort. Null = scan order (the manager's region/source walk). Clicking a header sorts
  // ascending; clicking the active header flips direction.
  const [sort, setSort] = useState(null);
  // Cloud Account id → display name, for rows that predate the manager stamping accountName.
  const [pickerNames, setPickerNames] = useState(null);
  useEffect(() => {
    let alive = true;
    Promise.resolve()
      .then(() => pickerCloudAccounts())
      .then((list) => {
        if (!alive || !Array.isArray(list)) return;
        const map = {};
        list.forEach((a) => {
          if (a && a.id && a.displayName) map[a.id] = a.displayName;
        });
        setPickerNames(map);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  const onSort = (key) =>
    setSort((prev) => (prev && prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  // Advanced view pref: hide internal-network (network-scoped) exposures. Client-side only —
  // always rendered from the full payload, so flipping it needs no re-scan. Persisted across
  // sessions. Default OFF (show everything).
  const [hideInternal, setHideInternal] = useState(getHideInternalPref);
  const toggleHideInternal = (checked) => {
    setHideInternal(checked);
    setHideInternalPref(checked);
  };

  // Re-narrow whenever the upstream row set or the route key changes.
  const routeKey = props.source;
  const upstream = props.managedSources;
  useEffect(() => {
    setRows(narrow(upstream, routeKey));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upstream, routeKey]);

  // Distinct source keys present in the (route-narrowed) set — drives the source filter chips.
  const sourceOptions = useMemo(() => {
    const set = new Set((rows || []).map((r) => r.source).filter(Boolean));
    return Array.from(set).sort();
  }, [rows]);

  // Keep a stale source filter from hiding everything if the current selection is no
  // longer present after an upstream refresh / route change.
  useEffect(() => {
    if (sourceFilter !== 'all' && !sourceOptions.includes(sourceFilter)) setSourceFilter('all');
  }, [sourceOptions, sourceFilter]);

  // Client-side source + free-text filtering on top of the route narrowing.
  const displayRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (rows || [])
      .filter((r) => sourceFilter === 'all' || r.source === sourceFilter)
      .filter((r) => matchesLifecycle(r, lifecycleFilter))
      .filter((r) => {
        if (!term) return true;
        return [r.identifier, r.name, r.database, r.host, r.engine, r.region, r.source, r.connectionUrl, r.instanceClass, accountLabel(r, pickerNames), r.accountNumber]
          .some((v) => String(v || '').toLowerCase().includes(term));
      });
  }, [rows, search, sourceFilter, lifecycleFilter, pickerNames]);

  // Match each resource to a saved data source (by shared host / DNS / object-store URL).
  const connectionFor = useMemo(() => {
    const list = Array.isArray(connections) ? connections : [];
    const map = new Map();
    (rows || []).forEach((r) => map.set(r.identifier, findMatchingConnection(r, list)));
    return map;
  }, [rows, connections]);

  const connectedCount = useMemo(
    () => displayRows.filter((r) => connectionFor.get(r.identifier)).length,
    [displayRows, connectionFor]
  );
  const filtering = sourceFilter !== 'all' || lifecycleFilter !== 'all' || search.trim() !== '';

  const toggle = async (row) => {
    if (props.fetchingData) return;
    const idx = rows.findIndex((r) => r.identifier === row.identifier);
    if (idx === -1) return;
    const newStatus = await startStopService(row);
    if (newStatus === 'not_supported') return;
    const next = rows.slice();
    next[idx] = { ...next[idx], status: newStatus };
    setRows(next);
    // Optimistic status above is immediate feedback only; ask the parent to poll the manager
    // until the resource reaches a terminal state, so 'stopping'/'starting' reconciles to
    // 'stopped'/'running' without a manual refresh (C2D-1001). Only when we actually moved into
    // a transitional state — skips read-only/no-op returns that leave status unchanged.
    if ((newStatus === 'starting' || newStatus === 'stopping') && props.onActionStarted) {
      props.onActionStarted();
    }
  };

  const rowList = useMemo(
    () => sortRows(displayRows, sort, (r) => (connectionFor.get(r.identifier) || {}).name, pickerNames),
    [displayRows, sort, connectionFor, pickerNames]
  );
  // The Account column only earns its space when the (route-narrowed) set spans more than one
  // account — single-account installs never see it.
  const multiAccount = useMemo(() => new Set((rows || []).map(accountKey)).size > 1, [rows]);
  const grid = multiAccount ? GRID_WITH_ACCOUNT : GRID;
  const minWidth = multiAccount ? MIN_WIDTH_WITH_ACCOUNT : MIN_WIDTH;
  const total = (rows || []).length;

  return (
    <Card className='w-100' style={{ border: 'none', background: 'transparent' }}>
      <div className='sg-small mb-2' style={{ color: INK[500] }}>
        {filtering ? `${rowList.length} of ${total}` : total} resource{total === 1 && !filtering ? '' : 's'} ·{' '}
        <span style={{ color: connectedCount > 0 ? STATUS.success.text : INK[500] }}>
          {connectedCount} connected as data source{connectedCount === 1 ? '' : 's'}
        </span>
      </div>
      <div className='d-flex align-items-center flex-wrap mb-3' style={{ gap: 10 }}>
        <Form.Control
          type='text'
          placeholder='Search id, name, host, engine, region…'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 320 }}
          data-testid='managed-search'
        />
        <div className='d-flex flex-wrap' style={{ gap: 6 }}>
          <Button
            size='sm'
            variant={sourceFilter === 'all' ? 'dark' : 'outline-secondary'}
            onClick={() => setSourceFilter('all')}
          >
            All
          </Button>
          {sourceOptions.map((src) => (
            <Button
              key={src}
              size='sm'
              variant={sourceFilter === src ? 'dark' : 'outline-secondary'}
              onClick={() => setSourceFilter(src)}
            >
              {sourceLabels[src] || String(src).replace(/_/g, ' ')}
            </Button>
          ))}
        </div>
        <Form.Control
          as='select'
          size='sm'
          value={lifecycleFilter}
          onChange={(e) => setLifecycleFilter(e.target.value)}
          aria-label='Filter by lifecycle state'
          title='Show only resources whose Manage toggle is on (running), off (stopped), or absent (lifecycle not manageable here).'
          style={{ width: 'auto', fontSize: 12.5, color: lifecycleFilter === 'all' ? INK[500] : INK[900] }}
          data-testid='lifecycle-filter'
        >
          {LIFECYCLE_FILTERS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Form.Control>
        <Form.Check
          type='switch'
          id='hide-internal-sources'
          className='ml-auto'
          style={{ fontSize: 12.5, color: INK[500] }}
          label='Hide internal-network sources'
          checked={hideInternal}
          onChange={(e) => toggleHideInternal(e.target.checked)}
          title='Hide port exposures whose source is inside the resource’s own network (security-group / VPC reference).'
        />
      </div>
      <div
        style={{
          background: '#fff',
          border: `1px solid ${INK[200]}`,
          borderRadius: 10,
          overflow: 'hidden',
          boxShadow: '0 1px 2px rgba(33,38,48,.06)',
        }}
      >
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth }}>
            <div
              className='sg-eyebrow'
              style={{
                display: 'grid',
                gridTemplateColumns: grid,
                padding: '10px 20px',
                borderBottom: `1px solid ${INK[150]}`,
                fontSize: 10.5,
                color: INK[400],
                alignItems: 'center',
              }}
            >
              <HeaderCell sortKey='connection' sort={sort} onSort={onSort}>C2 data source</HeaderCell>
              <HeaderCell sortKey='manage' sort={sort} onSort={onSort}>Manage</HeaderCell>
              <HeaderCell sortKey='name' sort={sort} onSort={onSort}>Name / ID</HeaderCell>
              <HeaderCell sortKey='type' sort={sort} onSort={onSort}>Type</HeaderCell>
              <HeaderCell sortKey='status' sort={sort} onSort={onSort}>Status</HeaderCell>
              <HeaderCell sortKey='region' sort={sort} onSort={onSort}>Region</HeaderCell>
              {multiAccount && (
                <HeaderCell sortKey='account' sort={sort} onSort={onSort}>Account</HeaderCell>
              )}
            </div>

            {rowList.map((row) => {
              const allExposures = Array.isArray(row.portExposures) ? row.portExposures : [];
              // The "Hide internal-network sources" pref filters the port rows/chips client-side.
              const exposures = hideInternal
                ? allExposures.filter((e) => !isInternalExposure(e))
                : allExposures;
              // Every row expands: the drawer carries engine/IP, class and created date, plus
              // port exposures when the scan found any.
              const openToInternet = exposures.some((e) => isLoudExposure(e));
              const isOpen = !!expanded[row.identifier];
              const semantic = statusToSemantic(row.status);
              const pill = sourcePillStyle(row.source);
              const label = sourceLabels[row.source] || String(row.source || '').replace(/_/g, ' ');
              const id = row.connectionType === 's3' ? row.host || row.name : row.identifier;
              // Name (or database) leads; the identifier sits beneath it. Resources with no
              // name — most EC2 instances — show the identifier alone as the lead line.
              const nameDb = row.database || row.name || '';
              const primary = nameDb || shortId(id);
              const secondary = nameDb && shortId(id) !== nameDb ? shortId(id) : null;
              return (
                <div key={row.identifier} style={{ borderBottom: `1px solid ${INK[100]}` }}>
                  <div
                    onClick={() => setExpanded((p) => ({ ...p, [row.identifier]: !p[row.identifier] }))}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: grid,
                      alignItems: 'center',
                      padding: '12px 20px',
                      cursor: 'pointer',
                    }}
                  >
                    <DataSourceCell row={row} connection={connectionFor.get(row.identifier)} />
                    <div>
                      <ManageToggle row={row} fetching={props.fetchingData} onToggle={toggle} />
                    </div>
                    <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span
                        title={exposures.length > 0 ? 'Show details and port exposures' : 'Show details'}
                        data-testid='row-expander'
                        style={{ color: INK[400], fontSize: 10, transition: 'transform .1s', transform: isOpen ? 'rotate(90deg)' : 'none' }}
                      >
                        ▶
                      </span>
                      {openToInternet && (
                        <BsExclamationTriangleFill
                          size={13}
                          color={STATUS.danger.text}
                          style={{ flexShrink: 0 }}
                          title={`Externally reachable — ${exposures.filter((e) => isLoudExposure(e)).length} port(s) opened to a source outside the private network. Expand for details.`}
                        />
                      )}
                      <div style={{ minWidth: 0 }}>
                        <div
                          data-testid='row-primary'
                          data-identifier={row.identifier}
                          title={nameDb ? `${nameDb} · ${row.identifier}` : row.identifier}
                          style={{ fontFamily: nameDb ? 'inherit' : FONT_MONO, fontSize: 12.5, fontWeight: 500, color: INK[900], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {primary}
                        </div>
                        {secondary ? (
                          <div
                            title={row.identifier}
                            style={{ fontFamily: FONT_MONO, fontSize: 11, color: INK[400], marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          >
                            {secondary}
                          </div>
                        ) : null}
                      </div>
                      <TagsPill tags={row.tags} resourceLabel={primary} />
                    </div>
                    <div style={{ textAlign: 'left', justifySelf: 'start' }}>
                      <Pill style={{ background: pill.bg, color: pill.color }}>{label}</Pill>
                    </div>
                    <div style={{ textAlign: 'left', justifySelf: 'start' }}>
                      {row.status ? (
                        <Pill style={{ background: STATUS[semantic].bg, color: STATUS[semantic].text }}>
                          {String(row.status).toUpperCase()}
                        </Pill>
                      ) : (
                        <span style={{ color: INK[400] }}>—</span>
                      )}
                    </div>
                    <div style={{ fontFamily: FONT_MONO, fontSize: 12, color: INK[700] }}>{row.region || '—'}</div>
                    {multiAccount && (
                      <div
                        data-testid='row-account'
                        title={accountTitle(row, pickerNames)}
                        style={{ fontSize: 12, color: row.accountName || (pickerNames && pickerNames[row.accountId]) ? INK[700] : INK[500], fontFamily: row.accountName || (pickerNames && pickerNames[row.accountId]) ? 'inherit' : FONT_MONO, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {accountLabel(row, pickerNames)}
                      </div>
                    )}
                  </div>
                  {isOpen && <RowDetails row={row} exposures={exposures} />}
                </div>
              );
            })}

            {rowList.length === 0 && (
              <div className='text-center sg-small' style={{ padding: 28, color: INK[400] }}>
                {total > 0
                  ? 'No resources match your search or filter.'
                  : 'No cloud resources detected. Use Actions ▾ → Refresh Sources to scan your enabled cloud providers.'}
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default ManagedSourcesTable;
